import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

/**
 * Module-level Stripe singleton. `loadStripe` must run exactly once per page load
 * (it injects the Stripe.js script), so it is hoisted out of the component to avoid
 * re-loading on every render/mount. Bound to the publishable key in the environment.
 */
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_ENV_KEY as string);

/** Props for {@link SubscriptionStripeCheckout}. */
export type SubscriptionStripeCheckoutProps = {
  /** Stripe session client secret used to mount the embedded checkout. */
  clientSecret: string;
  /** Fired by Stripe when the embedded checkout flow completes. */
  onComplete: () => void;
};

/**
 * SubscriptionStripeCheckout — reusable wrapper over Stripe's Embedded Checkout.
 *
 * Mounts `EmbeddedCheckoutProvider`/`EmbeddedCheckout` with the provided `clientSecret`
 * and wires `onComplete` to Stripe's completion callback. The `loadStripe` singleton is
 * resolved once at module scope. This component is presentation-only: it owns no session
 * state and performs no API calls — the caller generates the session and reacts to
 * `onComplete` (e.g. confirm-session + navigation).
 *
 * @example
 * <SubscriptionStripeCheckout clientSecret={clientSecret} onComplete={handleComplete} />
 */
export const SubscriptionStripeCheckout = ({ clientSecret, onComplete }: SubscriptionStripeCheckoutProps) => (
  <div className="w-full font-sans">
    <EmbeddedCheckoutProvider options={{ clientSecret, onComplete }} stripe={stripePromise}>
      <EmbeddedCheckout />
    </EmbeddedCheckoutProvider>
  </div>
);
